import React, { useState, useEffect } from "react";

interface BuildingDestinationFieldProps {
	destinationImmeuble: string;
	setDestinationImmeuble: (destinationImmeuble: string) => void;
	hasLargeStorageOrOffices?: string;
	setHasLargeStorageOrOffices?: (value: string) => void;
}

export default function BuildingDestinationField({
	destinationImmeuble,
	setDestinationImmeuble,
	hasLargeStorageOrOffices: externalHasLargeStorageOrOffices,
	setHasLargeStorageOrOffices: externalSetHasLargeStorageOrOffices,
}: BuildingDestinationFieldProps) {
	const [internalHasLargeStorageOrOffices, setInternalHasLargeStorageOrOffices] = useState<string>("");

	// Utiliser les valeurs externes si fournies, sinon les valeurs internes
	const hasLargeStorageOrOffices = externalHasLargeStorageOrOffices !== undefined
		? externalHasLargeStorageOrOffices
		: internalHasLargeStorageOrOffices;
	const setHasLargeStorageOrOffices = externalSetHasLargeStorageOrOffices || setInternalHasLargeStorageOrOffices;

	// Réinitialiser la réponse quand on change de destination
	useEffect(() => {
		if (destinationImmeuble !== "5") {
			setHasLargeStorageOrOffices("");
		}
	}, [destinationImmeuble]);

	return (
		<>
			<div className="gext-form__field gext-form__field--full">
				<label htmlFor="gext-destination-immeuble">
					Usage affecté à l'immeuble <span className="required">*</span>
				</label>
				<select
					id="gext-destination-immeuble"
					name="destination_immeuble"
					required
					value={destinationImmeuble}
					onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
						setDestinationImmeuble(e.target.value)
					}
				>
					<option value="">Veuillez sélectionner une destination</option>
					<option value="1">Exploitation agricole et forestière</option>
					<option value="2">Habitation</option>
					<option value="3">Commerce et activités de service</option>
					<option value="4">
						Équipements d'intérêt collectif et services publics
					</option>
					<option value="5">
						Autres activités des secteurs secondaire ou tertiaire
					</option>
				</select>
				{getDestinationImmeubleDescription(destinationImmeuble)}
			</div>

			{/* Question conditionnelle pour Autres activités des secteurs secondaire ou tertiaire */}
			{destinationImmeuble === "5" && (
				<div className="gext-form__field gext-form__field--full">
					<label>
						Votre établissement dispose-t-il de bureaux ou d'espaces de
						stockage représentant 1000 m² ou plus ?{" "}
						<span className="required">*</span>
					</label>
					<div className="gext-form__radio-group">
						<label className="gext-form__radio-option">
							<input
								type="radio"
								name="has_large_storage_or_offices"
								value="yes"
								required={destinationImmeuble === "5"}
								checked={hasLargeStorageOrOffices === "yes"}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									setHasLargeStorageOrOffices(e.target.value)
								}
							/>
							<span>Oui</span>
						</label>
						<label className="gext-form__radio-option">
							<input
								type="radio"
								name="has_large_storage_or_offices"
								value="no"
								required={destinationImmeuble === "5"}
								checked={hasLargeStorageOrOffices === "no"}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									setHasLargeStorageOrOffices(e.target.value)
								}
							/>
							<span>Non</span>
						</label>
					</div>
				</div>
			)}
		</>
	);
}

function getDestinationImmeubleDescription(destinationImmeuble) {
	let content: React.ReactNode = null;
	switch (destinationImmeuble) {
		case "1":
			content = (
				<ul>
					<li>exploitation agricole</li>
					<li>exploitation forestière</li>
				</ul>
			);
			break;
		case "2":
			content = (
				<ul>
					<li>logement</li>
					<li>hébergement</li>
				</ul>
			);
			break;
		case "3":
			content = (
				<ul>
					<li>artisanat et commerce de détail</li>
					<li>restauration</li>
					<li>commerce de gros</li>
					<li>
						activités de services où s'effectue l'accueil d'une
						clientèle
					</li>
					<li>
						cinéma, hôtels (définis par l’arrêté du 10 novembre 2016
						comme des «constructions destinées à l’accueil de
						touristes dans des hôtels, c’est-à-dire des
						établissements commerciaux qui offrent à une clientèle
						de passage qui, sauf exception, n’y élit pas domicile,
						des chambres ou des appartements meublés en location,
						ainsi qu’un certain nombre de services»);
					</li>
					<li>
						autres hébergements touristiques (défini par l’arrêté du
						10 novembre 2016 comme des « constructions autres que
						les hôtels destinées à accueillir des touristes,
						notamment les résidences de tourisme et les villages de
						vacances, ainsi que les constructions dans les terrains
						de camping et dans les parcs résidentiels de loisirs »).
					</li>
				</ul>
			);
			break;
		case "4":
			content = (
				<ul>
					<li>
						locaux et bureaux accueillant du public des
						administrations publiques et assimilés
					</li>
					<li>
						locaux techniques et industriels des administrations
						publiques et assimilés
					</li>
					<li>
						établissements d'enseignement, de santé et d'action
						sociale
					</li>
					<li>salles d'art et de spectacles</li>
					<li>équipements sportifs</li>
					<li>autres équipements recevant du public</li>
				</ul>
			);
			break;
		case "5":
			content = (
				<ul>
					<li>industrie</li>
					<li>entrepôt</li>
					<li>bureau</li>
					<li>centre de congrès et d'exposition</li>
					<li>cuisine dédiée à la vente en ligne</li>
				</ul>
			);
			break;
		default:
			return null;
	}

	return <div className="gext-form__field__description">{content}</div>;
}
